import React, {useMemo, useRef, useState } from "react";
import {RCCDropDownItem, RCCGrid, RCCHidden, RCCHookToast, RCCIcon, RCCServicesApi } from "react-common-components";
import { useHistory } from "react-router-dom";
import Accordion from "src/components/Accordion";
import CheckPermission from "src/components/CheckPermission";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import ListCards from "src/components/ListCards";
import Loader from "src/components/Loader";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { standardErrorMessages } from "src/utils/errors";
import formatLink from "src/utils/formatLink";
import { FormMeetingAction, IGrouperOfMeetings, IMeetingListDTO, IRefModalDeleteMeeting, IRefModalParticipantAttend } from "../../interface";
import ListOfMeetings from "../listOfMeetings";
import MeetingCard from "../meetingCard";
import ModalDeleteMeeting from "../modalDeleteMeeting";
import ModalParticipantAttend from "../modalParticipantAttend";
import { GrouperFilterButton } from "./styles";

const GrouperOfMeetings: React.FC<IGrouperOfMeetings> = ({
	filters,
	grouper,
	groupersSelect,
	layoutList,
	loadGroupers,
	modalRef
}) => {

	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const {user,administrator,superUser, checkIfHasSpecificPermission} = useAuth();
	const history = useHistory();

	const [meetingList,setMeetingList] = useState<IMeetingListDTO[]>([]);
	const [displayMeetingList,setDisplayMeetingList] = useState<IMeetingListDTO[]>([]);
	const [filterSituation,setFilterSituation] = useState<string>();
	const [loading, setLoading] = useState(false);

	const refmodalDelete = useRef<IRefModalDeleteMeeting>(null);
	const refmodalParticipant = useRef<IRefModalParticipantAttend>(null);
	
	const situationFilterHandle = (situation: string) => {
		let situationToUpdate:string|undefined = situation;

		if(filterSituation && filterSituation === situation)
			situationToUpdate = undefined;
		
		setFilterSituation(situationToUpdate);
	};

	const handleAccordion = (expanded:boolean) => {
		if(!expanded)
			return;

		executeQuery();
	};

	const executeQuery = async() => {
		setLoading(true);
		try {
			const dataController = await RCCServicesApi.getRequest({
				method: "Meetings/List",
				urlParameters: {
					group:groupersSelect,
					id:grouper.id,
					id2:grouper.id2,
					...filters
				}
			});
			setMeetingList(dataController.data);
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar reuniões"), addToast);
			setLoading(false);
		}
	};

	const options = (row: any) => {

		const hasPermission = (reponsible:string) => superUser || administrator || reponsible.toString() === user.efa005Id.toString();
		const canCreateUpdate = checkIfHasSpecificPermission("/meeting/panel", PermissionEnum.CREATE_UPDATE);

		const optionsDefault = () => (
			<>
				{(row.ara001Linkexterno && layoutList === "list")&& (
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {
							window.open(formatLink(row.ara001Linkexterno), "_blank");
						}}
					>
						<RCCIcon name="linkExternal" />
						Link
					</RCCDropDownItem>
				)}
				<RCCDropDownItem 
					selfEnclose
					onClick={() => {modalRef.current?.actionButtonRequest(row.ara001Id,FormMeetingAction.VIEW);}}
				>
					<RCCIcon name="eye" 
					/>
					{translate("Visualizar")}
				</RCCDropDownItem>
				{row.ara001Finalizada === "S" &&(
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {history.push(`/meeting/print/${row.ara001Id}`);}}
					>
						<RCCIcon name="print" 
						/>
						{translate("Imprimir")}
					</RCCDropDownItem>
				)}
				{(row.participantCanAttend && row.isParticipant)&& (
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {refmodalParticipant.current?.actionButtonOpen(row.ara001Id,() => {});}}
					>
						<RCCIcon name="user" 
						/>
						{translate("Comparecer")}
					</RCCDropDownItem>
				)}
			</>
		);

		return (	
			<OptionsColumn>
				{
					hasPermission(row.ara001Efa005Id) || canCreateUpdate ? 
						<>
							{(row.ara001Finalizada === "S") && (
								<RCCDropDownItem
									selfEnclose
									onClick={() => {modalRef.current?.actionButtonRequest(row.ara001Id,FormMeetingAction.REOPEN,() => loadGroupers());}}
								>
									<RCCIcon name="pencil"/>
									{translate("Reabrir")}
								</RCCDropDownItem>
							)}
							{(row.ara001Finalizada !== "S") && (
								<>
									<RCCDropDownItem
										selfEnclose
										onClick={() => {modalRef.current?.actionButtonRequest(row.ara001Id,FormMeetingAction.CHANGE,() => loadGroupers());}}
									>
										<RCCIcon name="pencil"/>
										{translate("Alterar")}
									</RCCDropDownItem>
									<RCCDropDownItem
										selfEnclose
										onClick={() => {modalRef.current?.actionButtonRequest(row.ara001Id,FormMeetingAction.EXECUTE,() => loadGroupers());}}
									>
										<RCCIcon name="play"/>
										{translate("Executar reunião")}
									</RCCDropDownItem>
								</>
							)}
							<RCCDropDownItem 
								selfEnclose
								onClick={() => {modalRef.current?.actionButtonRequest(row.ara001Id,FormMeetingAction.COPY,() => loadGroupers());}}
							>
								<RCCIcon name="copy" />
								{translate("Copiar")}
							</RCCDropDownItem>

							{optionsDefault()}
						</>

						:

						<>
							{optionsDefault()}
						</>
				}

				
				{hasPermission(row.ara001Efa005Id) && (
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => { refmodalDelete.current?.actionButtonDelete(row,() => loadGroupers()); }}
						>
							<RCCIcon name="trash" 
							/>
							{translate("Excluir")}
						</RCCDropDownItem>
					</CheckPermission>
				)}
			</OptionsColumn>
		);

	};
	
	const Meetings = useMemo(() => (
		<>
			{layoutList === "list" ? 
				<ListOfMeetings 
					data={displayMeetingList}
					options={options}
					callBackFunction={loadGroupers}
				/>
				:
				<ListCards 
					data={displayMeetingList}
					mapper={(meeting : IMeetingListDTO) => (
						<MeetingCard
							data={meeting}
							options={options}
							key={`${meeting.ara001Id}`}
							callBackFunction={loadGroupers}
						>
						</MeetingCard>	
					)}
				/>		
			}
		</>
	), [displayMeetingList, layoutList, grouper]);

	useEffectSkipInitialRender(() => {
		setDisplayMeetingList(
			meetingList.filter((x) => filterSituation ? x.ara001Finalizada === filterSituation : true)
		);
	}, [meetingList,filterSituation]);	

	return (
		<>
			<Accordion 
				onChange={(_, expanded) => handleAccordion(expanded)}
				header={
					<RCCGrid 
						container
						item 
						alignItems="center"
						justifyContent="space-between"
					>
						<span style={{ display: "flex", alignItems: "center"}}>
							{grouper.image !== null && (
								<Photo 
									hover
									name={grouper.imageName || ""}
									link={grouper.image !== "" ? `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${grouper.image}` : ""}
								/>
							)}
							{grouper.title}
						</span>
						<RCCGrid container justifyContent="flex-end">
							{(grouper.finished > 0) && (
								<GrouperFilterButton 
									color="green" 
									isActive={!!(filterSituation && filterSituation === "S")}
									onClick={(e) => { e.stopPropagation(); situationFilterHandle("S"); }}
								>
									{grouper.finished} <RCCHidden only="xs">{translate("Finalizada(s)")}</RCCHidden> 
								</GrouperFilterButton>
							)}
							{(grouper.notFinished > 0) && (
								<GrouperFilterButton 
									color="red" 
									isActive={!!(filterSituation && filterSituation === "N")}
									onClick={(e) => { e.stopPropagation(); situationFilterHandle("N"); }}
								>
									{grouper.notFinished} <RCCHidden only="xs">{translate("Não finalizada(s)")}</RCCHidden> 
								</GrouperFilterButton>
							)}
							<Title color="purple" size="sm" styleInline="margin: 0 4px;">{translate("Total")}: {grouper.count}</Title>
						</RCCGrid>
					</RCCGrid>
				}
			>		
				<>
					{loading ? 
						<Loader/>
						:
						Meetings
					}
				</>
			</Accordion>
			<ModalDeleteMeeting ref={refmodalDelete}/>
			<ModalParticipantAttend ref={refmodalParticipant}/>
		</>
	);
};

export default GrouperOfMeetings;
